/**
 * <jersey-customiser>: jersey name/number personalisation with a live preview.
 * A custom element so it re-initialises whenever the theme re-renders product info,
 * and its inputs submit with the product form through their `form` attribute.
 */
if (!customElements.get('jersey-customiser')) {
  customElements.define(
    'jersey-customiser',
    class JerseyCustomiser extends HTMLElement {
      connectedCallback() {
        this.toggleButton = this.querySelector('.jersey-customisation__toggle');
        this.formSection = this.querySelector('.jersey-customisation__form');
        this.nameInput = this.querySelector('[data-jersey-name]');
        this.numberInput = this.querySelector('[data-jersey-number]');
        this.feeInput = this.querySelector('.jersey-customisation__fee-input');
        this.previewName = this.querySelector('.jersey-preview__name');
        this.previewNumber = this.querySelector('.jersey-preview__number');

        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.toggleButton.addEventListener('click', () => this.toggle(), { signal });
        this.nameInput.addEventListener('input', () => this.onNameInput(), { signal });
        this.nameInput.addEventListener('change', () => this.onNameChange(), { signal });
        this.numberInput.addEventListener('input', () => this.onNumberInput(), { signal });
        // Capture phase runs before product-form.js, so invalid values never reach the cart.
        document.addEventListener('submit', (event) => this.onSubmit(event), { capture: true, signal });

        // Re-open if the browser restored values (e.g. back navigation).
        this.toggle(Boolean(this.nameInput.value || this.numberInput.value));
      }

      disconnectedCallback() {
        this.abortController.abort();
      }

      toggle(expand = this.formSection.hidden) {
        this.formSection.hidden = !expand;
        this.toggleButton.setAttribute('aria-expanded', expand);

        if (!expand) {
          this.nameInput.value = '';
          this.numberInput.value = '';
        }

        this.update();
      }

      onNameInput() {
        const value = this.nameInput.value.toUpperCase().replace(/[^A-Z\s\-']/g, '');
        if (value !== this.nameInput.value) this.nameInput.value = value;
        this.update();
      }

      onNameChange() {
        this.nameInput.value = this.nameInput.value.trim().replace(/\s+/g, ' ');
        this.update();
      }

      onNumberInput() {
        const value = this.numberInput.value.replace(/\D/g, '');
        if (value !== this.numberInput.value) this.numberInput.value = value;
        this.update();
      }

      onSubmit(event) {
        // Not `event.target.id`: the product form's `<input name="id">` shadows that property.
        if (event.target.getAttribute('id') !== this.dataset.formId) return;

        const invalidInput = [this.nameInput, this.numberInput].find((input) => !input.checkValidity());
        if (!invalidInput) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        invalidInput.reportValidity();
      }

      update() {
        const name = this.nameInput.value.trim();
        const number = this.numberInput.value;
        const { min, max } = this.numberInput.dataset;
        const inRange = number === '' || (Number(number) >= Number(min) && Number(number) <= Number(max));

        this.numberInput.setCustomValidity(inRange ? '' : `Choose a number between ${min} and ${max}.`);
        if (this.feeInput) this.feeInput.disabled = !(name || number);

        this.previewName.textContent = name || 'YOUR NAME';
        this.previewName.classList.toggle('jersey-preview__name--empty', !name);
        this.previewName.style.fontSize = `${this.nameFontSize(name.length || 9)}px`;

        this.previewNumber.textContent = number || '00';
        this.previewNumber.classList.toggle('jersey-preview__number--empty', !number);
      }

      nameFontSize(length) {
        if (length > 12) return 9;
        if (length > 10) return 11;
        if (length > 8) return 12;
        if (length > 6) return 13;
        return 14;
      }
    }
  );
}
